import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { SITE_URLS, CONTACT_INFO, DEFAULT_WHATSAPP } from "../util/constants";

/**
 * Componente para gerenciar meta tags e SEO dinâmico
 * @param {object} props
 * @param {string} props.title - Título da página (opcional, será gerado automaticamente se não fornecido)
 * @param {string} props.description - Descrição da página (opcional, será gerado automaticamente se não fornecido)
 * @param {string} props.keywords - Palavras-chave adicionais (opcional)
 * @param {string} props.image - URL da imagem para Open Graph (opcional)
 * @param {string} props.type - Tipo de conteúdo (website, article, etc.)
 */
const SEO = ({
  title,
  description,
  keywords,
  image,
  type = "website",
}) => {
  const location = useLocation();
  const baseUrl = SITE_URLS.BASE || window.location.origin;
  const currentUrl = `${baseUrl}${location.pathname}`;
  const defaultImage = `${baseUrl}/assets/img/logo.png`;

  // Informações base da empresa (memoizado para evitar recriação)
  const companyInfo = useMemo(() => ({
    name: "Grupo Terra",
    legalName: "Matrix Terra",
    description: "Fornecendo qualidade e lucro no segmento de mecânica pesada há 30 anos. Especialistas em ferramentas, fixadores, químicos e produtos para oficinas mecânicas pesadas.",
    foundingDate: "1994",
    industry: "Distribuição de produtos para mecânica pesada",
    contactPhone: `+55${CONTACT_INFO.PHONE.WHATSAPP}`,
    contactEmail: CONTACT_INFO.EMAIL.CONTACT,
    whatsapp: DEFAULT_WHATSAPP,
    address: {
      "@type": "PostalAddress",
      addressCountry: "BR",
      addressLocality: "Brasil",
    },
  }), []);

  // Gera título e descrição baseado na rota
  const getPageMetadata = () => {
    const path = location.pathname;

    // Títulos e descrições por página
    const pageMetadata = {
      "/": {
        title: "Grupo Terra - Fornecendo qualidade e lucro no segmento de mecânica pesada há 30 anos",
        description: "Grupo Terra: mais de 30 anos de experiência em fornecimento de miudezas, químicos e ferramentas para oficinas mecânicas pesadas. Mais de 7 mil produtos de diferentes segmentos. Atendimento especializado e valores cristãos.",
        keywords: "mecânica pesada, ferramentas, fixadores, químicos, estoque, oficinas mecânicas, produtos automotivos, distribuição, Grupo Terra, Matrix Terra",
      },
      "/produtos": {
        title: "Produtos - Catálogo Completo | Grupo Terra",
        description: "Explore nosso catálogo com mais de 7 mil produtos para mecânica pesada: ferramentas profissionais, fixadores, produtos químicos e muito mais. Encontre o que precisa para sua oficina.",
        keywords: "produtos mecânica pesada, catálogo ferramentas, fixadores, químicos, produtos automotivos, estoque",
      },
      "/videos": {
        title: "Produtos com Vídeo - Materiais Complementares | Grupo Terra",
        description: "Explore nossos produtos que possuem materiais em vídeo para melhor compreensão. Vídeos explicativos, tutoriais e demonstrações de produtos para mecânica pesada.",
        keywords: "produtos com vídeo, tutoriais, demonstrações, materiais complementares, vídeos explicativos, mecânica pesada",
      },
      "/autenticacao": {
        title: "Login - Acesse sua Conta | Grupo Terra",
        description: "Acesse sua conta no Grupo Terra para visualizar produtos, fazer pedidos e gerenciar sua conta.",
        keywords: "login, acesso, conta, cliente",
      },
      "/carrinho": {
        title: "Carrinho de Compras | Grupo Terra",
        description: "Revise seus produtos no carrinho de compras. Finalize seu pedido com segurança.",
        keywords: "carrinho, compras, pedido",
      },
      "/minha-conta": {
        title: "Minha Conta - Área do Cliente | Grupo Terra",
        description: "Gerencie sua conta, visualize histórico de pedidos, colaboradores e informações de pagamento e entrega.",
        keywords: "minha conta, área do cliente, pedidos, histórico",
      },
      "/redefinir-senha": {
        title: "Redefinir Senha | Grupo Terra",
        description: "Redefina sua senha de acesso à sua conta no Grupo Terra de forma segura.",
        keywords: "redefinir senha, recuperar senha, alterar senha",
      },
    };

    // Verifica se é uma página de detalhes de produto
    // O título e descrição serão atualizados dinamicamente pela página de detalhes
    if (path.startsWith("/produto/")) {
      return {
        title: "Detalhes do Produto | Grupo Terra",
        description: "Visualize detalhes completos do produto, especificações técnicas e informações para sua oficina de mecânica pesada.",
        keywords: "detalhes produto, especificações, mecânica pesada",
      };
    }

    // Retorna metadata da página ou padrão
    return pageMetadata[path] || {
      title: `${companyInfo.name} - ${companyInfo.industry}`,
      description: companyInfo.description,
      keywords: "mecânica pesada, ferramentas, fixadores, químicos, Grupo Terra",
    };
  };

  const pageMeta = getPageMetadata();
  const finalTitle = title || pageMeta.title;
  const finalDescription = description || pageMeta.description;
  const finalKeywords = keywords 
    ? `${pageMeta.keywords}, ${keywords}` 
    : pageMeta.keywords;
  const finalImage = image || defaultImage;

  useEffect(() => {
    // Atualiza título
    document.title = finalTitle;

    // Função auxiliar para atualizar ou criar meta tag
    const setMetaTag = (name, content, attribute = "name") => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    // Meta tags básicas
    setMetaTag("description", finalDescription);
    setMetaTag("keywords", finalKeywords);
    setMetaTag("author", companyInfo.name);
    setMetaTag("robots", "index, follow");
    setMetaTag("viewport", "width=device-width, initial-scale=1.0");
    setMetaTag("language", "pt-BR");
    setMetaTag("geo.region", "BR");

    // Open Graph
    setMetaTag("og:type", type, "property");
    setMetaTag("og:url", currentUrl, "property");
    setMetaTag("og:title", finalTitle, "property");
    setMetaTag("og:description", finalDescription, "property");
    setMetaTag("og:image", finalImage, "property");
    setMetaTag("og:image:secure_url", finalImage.startsWith("https://") ? finalImage : finalImage.replace("http://", "https://"), "property");
    setMetaTag("og:image:alt", finalTitle, "property");
    setMetaTag("og:site_name", companyInfo.name, "property");
    setMetaTag("og:locale", "pt_BR", "property");

    // Twitter Card
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:url", currentUrl);
    setMetaTag("twitter:title", finalTitle);
    setMetaTag("twitter:description", finalDescription);
    setMetaTag("twitter:image", finalImage.startsWith("https://") ? finalImage : finalImage.replace("http://", "https://"));
    setMetaTag("twitter:image:alt", finalTitle);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", currentUrl);

    // Structured Data (JSON-LD) - Organization
    let orgScript = document.querySelector('script[data-jsonld="organization"]');
    if (!orgScript) {
      orgScript = document.createElement("script");
      orgScript.setAttribute("type", "application/ld+json");
      orgScript.setAttribute("data-jsonld", "organization");
      document.head.appendChild(orgScript);
    }
    orgScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: companyInfo.name,
      legalName: companyInfo.legalName,
      url: baseUrl,
      logo: {
        "@type": "ImageObject",
        url: defaultImage,
      },
      description: companyInfo.description,
      foundingDate: companyInfo.foundingDate,
      address: companyInfo.address,
      contactPoint: {
        "@type": "ContactPoint",
        telephone: companyInfo.contactPhone,
        contactType: "customer service",
        email: companyInfo.contactEmail,
        availableLanguage: "Portuguese",
      },
      sameAs: [
        // Adicione redes sociais aqui se houver
      ],
      areaServed: {
        "@type": "Country",
        name: "Brasil",
      },
    });

    // Structured Data (JSON-LD) - WebSite
    let websiteScript = document.querySelector('script[data-jsonld="website"]');
    if (!websiteScript) {
      websiteScript = document.createElement("script");
      websiteScript.setAttribute("type", "application/ld+json");
      websiteScript.setAttribute("data-jsonld", "website");
      document.head.appendChild(websiteScript);
    }
    websiteScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: companyInfo.name,
      url: baseUrl,
      description: companyInfo.description,
      potentialAction: {
        "@type": "SearchAction",
        target: {
          "@type": "EntryPoint",
          urlTemplate: `${baseUrl}/produtos?search={search_term_string}`,
        },
        "query-input": "required name=search_term_string",
      },
    });

    // Structured Data (JSON-LD) - LocalBusiness (se aplicável)
    if (location.pathname === "/") {
      let businessScript = document.querySelector('script[data-jsonld="localbusiness"]');
      if (!businessScript) {
        businessScript = document.createElement("script");
        businessScript.setAttribute("type", "application/ld+json");
        businessScript.setAttribute("data-jsonld", "localbusiness");
        document.head.appendChild(businessScript);
      }
      businessScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: companyInfo.name,
        description: companyInfo.description,
        url: baseUrl,
        telephone: companyInfo.contactPhone,
        email: companyInfo.contactEmail,
        priceRange: "$$",
        areaServed: {
          "@type": "Country",
          name: "Brasil",
        },
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Produtos para Mecânica Pesada",
          itemListElement: [
            {
              "@type": "OfferCatalog",
              name: "Ferramentas",
              description: "Ferramentas profissionais para mecânica pesada",
            },
            {
              "@type": "OfferCatalog",
              name: "Fixadores",
              description: "Produtos de fixação robustos e duráveis",
            },
            {
              "@type": "OfferCatalog",
              name: "Químicos",
              description: "Produtos químicos para manutenção e limpeza",
            },
          ],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalTitle, finalDescription, finalKeywords, finalImage, currentUrl, type, baseUrl, defaultImage, location.pathname, companyInfo]);

  return null;
};

export default SEO;
